import { forwardRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { useT } from '../../i18n';

interface SidebarSearchProps {
  giaTri: string;
  onDoi: (v: string) => void;
  /** Số mục lá còn lại sau khi lọc, để thông báo cho trình đọc màn hình. */
  soKetQua: number;
  /** Bàn phím: ↑ ↓ chọn, Enter mở, Esc xoá. */
  onXuongPhim: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Ô tìm nhanh trong thanh điều hướng.
 *
 * Vì sao cần: cây có 12 nhóm và ~59 mục. Đi tìm bằng mắt qua một cột dài hơn
 * hai lần chiều cao màn hình là việc người dùng phải làm lại mỗi lần đổi màn,
 * còn gõ `chung tu` thì ra ngay. Đây là đường đi nhanh, **không thay** cây:
 * xoá chữ là cây trở lại đúng như cũ.
 *
 * Dùng `<input>` thuần chứ không `Input` của antd: ô này sống trong thanh điều
 * hướng với nền và viền riêng, mà `Input` mang theo nền `colorBgContainer` và
 * chiều cao của biểu mẫu — ghi đè lại tốn nhiều class hơn là tự dựng. Đây là
 * **ngoại lệ có lý do**, không phải lối tắt: ô nhập trong biểu mẫu vẫn phải
 * dùng `Input` trong `<Field>`.
 */
export const SidebarSearch = forwardRef<HTMLInputElement, SidebarSearchProps>(
  function SidebarSearch({ giaTri, onDoi, soKetQua, onXuongPhim }, ref) {
    const { t } = useT();
    const dangTim = giaTri.trim().length > 0;

    return (
      // Lề ngang do hàng flex ở `Sidebar.tsx` lo; `min-w-0` để ô co được khi
      // nút mở/gập hết chiếm chỗ bên cạnh.
      <div className="min-w-0 flex-1">
        {/* `role="search"` cho trình đọc màn hình nhảy thẳng tới đây. */}
        <div role="search" className="relative">
          <Search
            aria-hidden
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted"
          />
          <input
            ref={ref}
            type="search"
            value={giaTri}
            onChange={(e) => onDoi(e.target.value)}
            onKeyDown={onXuongPhim}
            // `aria-label` thay cho `<label>` hiện: thanh điều hướng không có
            // chỗ cho một nhãn nữa, mà ô tìm thì ai cũng hiểu qua biểu tượng.
            aria-label={t('khung.timTrongDieuHuong')}
            placeholder={t('khung.timManHinh')}
            // Cố ý **không** `role="combobox"`: vùng kết quả là một `<nav>`
            // chứa liên kết thật, không phải listbox (xem ghi chú ở
            // `Sidebar.tsx`). Khai combobox mà không có listbox hợp lệ phía sau
            // thì trình đọc màn hình hứa một thứ không tồn tại.
            aria-controls="sidebar-ket-qua"
            // `type="search"` ở WebKit tự thêm nút xoá riêng, nằm đè lên nút
            // `×` của ta. Tắt bằng `appearance-none` ở lớp CSS `.nav-tim`.
            className={cn(
              'nav-tim h-8 w-full rounded border border-default bg-subtle pl-8 pr-7 text-sm',
              'text-content-primary placeholder:text-content-muted',
              'transition-colors hover:border-strong',
              'focus:border-primary-500 focus:bg-surface focus:outline-none focus:ring-1 focus:ring-primary-100',
            )}
          />
          {dangTim && (
            <button
              type="button"
              onClick={() => onDoi('')}
              title={t('khung.xoaTuKhoa')}
              aria-label={t('khung.xoaTuKhoaTimKiem')}
              className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-content-muted transition-colors hover:bg-surface hover:text-content-primary"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/*
          Số kết quả đọc lên cho trình đọc màn hình.
          `aria-live="polite"` chứ không `assertive`: người dùng đang gõ, cắt
          ngang từng ký tự thì không nghe được gì cả.
        */}
        <p aria-live="polite" className="sr-only">
          {dangTim ? t('khung.soKetQua', { so: soKetQua }) : ''}
        </p>
      </div>
    );
  },
);

/**
 * Phím tắt mở ô tìm: `/` và `Ctrl/⌘ + K`.
 *
 * `/` là phím tắt tìm kiếm quen của GitHub, Linear, Slack; `⌘K` là của bảng
 * lệnh. Nhận cả hai vì người dùng mang theo quen tay từ công cụ khác.
 *
 * ⚠️ Phải bỏ qua khi tiêu điểm đang ở ô nhập, vùng soạn thảo hay `contenteditable`:
 * không thì người đang gõ `/` vào ô tìm của một màn hình nào đó sẽ bị giật
 * tiêu điểm sang thanh điều hướng — một lỗi rất khó lần ra.
 */
export function useMoOTim(moVaChonHet: () => void): void {
  useEffect(() => {
    function xuLy(e: KeyboardEvent) {
      const dich = e.target as HTMLElement | null;
      const tag = dich?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        dich?.isContentEditable
      ) {
        return;
      }

      const laCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      const laGach = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (!laCmdK && !laGach) return;

      // `/` phải chặn mặc định, nếu không Firefox mở thanh tìm nhanh của nó.
      e.preventDefault();
      moVaChonHet();
    }

    window.addEventListener('keydown', xuLy);
    return () => window.removeEventListener('keydown', xuLy);
  }, [moVaChonHet]);
}
